import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  AreaChart, Area, Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
  Legend, Line, LineChart,
} from "recharts";
import {
  BarChart3, Download, LayoutDashboard, RadioTower, HeartPulse, Swords, FolderOpen,
  Save, Clock, FolderKanban, ExternalLink, Reply, Bookmark, Heart, Repeat, MessageCircle,
} from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Sparkline } from "@/components/feature/sparkline";
import { SocialDot } from "@/components/feature/social-dot";
import { useProjectAnalytics } from "@/hooks/use-dashboard";
import { useProjects } from "@/hooks/use-projects";
import { useActiveProject } from "@/hooks/use-active-project";
import { api, type NetworkBreakdown, type ProjectAnalytics, type TopNetworkPost } from "@/lib/api";
import { cn, formatNumber } from "@/lib/utils";

export const Route = createFileRoute("/_auth/analyze")({
  component: AnalyzePage,
});

type Tab = "overview" | "networks" | "sentiment" | "reports";
const TABS: { id: Tab; label: string; Icon: typeof LayoutDashboard }[] = [
  { id: "overview", label: "Resumen general", Icon: LayoutDashboard },
  { id: "networks", label: "Por red social", Icon: RadioTower },
  { id: "sentiment", label: "Sentimiento", Icon: HeartPulse },
  { id: "reports", label: "Informes guardados", Icon: FolderOpen },
];
const RANGES: { id: number; label: string }[] = [
  { id: 1, label: "Hoy" },
  { id: 7, label: "7 días" },
  { id: 30, label: "30 días" },
  { id: 90, label: "90 días" },
];

// Network metadata for color + label
const NETWORK_META: Record<string, { color: string; label: string }> = {
  x: { color: "#0EA5E9", label: "X (Twitter)" },
  fb: { color: "#1877F2", label: "Facebook" },
  ig: { color: "#E1306C", label: "Instagram" },
  tt: { color: "#111827", label: "TikTok" },
  li: { color: "#0A66C2", label: "LinkedIn" },
  yt: { color: "#FF0000", label: "YouTube" },
  reddit: { color: "#FF4500", label: "Reddit" },
  news: { color: "#6B7280", label: "Noticias" },
};

function netMeta(network: string) {
  return NETWORK_META[network] || { color: "#6B7280", label: network };
}

// ============================================================
// Main page
// ============================================================

function AnalyzePage() {
  const { activeId } = useActiveProject();
  const { data: projects } = useProjects({ limit: 200 });
  const [days, setDays] = useState(30);
  const [tab, setTab] = useState<Tab>("overview");

  const activeProject = projects?.find((p) => p.id === activeId) ?? null;
  const analytics = useProjectAnalytics(activeId, days);

  if (!projects) {
    return (
      <div className="px-6 py-6 max-w-[1480px] mx-auto space-y-4">
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }
  if (projects.length === 0) {
    return (
      <div className="px-6 py-6 max-w-[1480px] mx-auto">
        <h1 className="text-2xl font-bold text-ink-900 dark:text-white mb-2">Analizar</h1>
        <p className="text-sm text-ink-500 mb-4">No tienes proyectos todavía. Crea uno para empezar.</p>
        <Link to="/monitor">
          <Button>Ir a Monitorización</Button>
        </Link>
      </div>
    );
  }
  if (!activeProject) {
    return (
      <div className="px-6 py-6 max-w-[1480px] mx-auto">
        <p className="text-sm text-ink-500">Selecciona un proyecto en el selector superior.</p>
      </div>
    );
  }

  return (
    <div className="px-6 py-6 max-w-[1480px] mx-auto">
      {/* Project header */}
      <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
        <div>
          <p className="text-xs text-ink-500 mb-1 flex items-center gap-1.5">
            <FolderKanban size={11} /> Analytics del proyecto
          </p>
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={
                activeProject.color
                  ? { backgroundColor: `${activeProject.color}1A`, color: activeProject.color }
                  : { backgroundColor: "#EFF6FF", color: "#2563EB" }
              }
            >
              <BarChart3 size={18} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-ink-900 dark:text-white">
                {activeProject.name}
              </h1>
              <p className="text-sm text-ink-500 mt-0.5 flex items-center gap-2">
                <span>
                  {activeProject.networks.length}{" "}
                  {activeProject.networks.length === 1 ? "red configurada" : "redes configuradas"}
                </span>
                <span>·</span>
                <span className="flex items-center gap-1">
                  {activeProject.networks.map((n) => (
                    <SocialDot key={n} network={n} size={12} />
                  ))}
                </span>
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex bg-ink-100 dark:bg-ink-800 rounded-lg p-0.5 text-xs">
            {RANGES.map((r) => (
              <button
                key={r.id}
                onClick={() => setDays(r.id)}
                className={cn(
                  "h-7 px-2.5 rounded-md transition-colors",
                  days === r.id
                    ? "bg-white dark:bg-ink-900 shadow-sm text-ink-900 dark:text-white font-medium"
                    : "text-ink-500",
                )}
              >
                {r.label}
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" disabled>
            <Save size={14} /> Guardar
          </Button>
          <Button variant="outline" size="sm" disabled>
            <Clock size={14} /> Programar
          </Button>
          <a
            href={api.exportMentionsUrl({ project_id: activeProject.id, limit: 5000 })}
            target="_blank"
            rel="noreferrer"
          >
            <Button size="sm">
              <Download size={14} /> Exportar
            </Button>
          </a>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-ink-200 dark:border-ink-800 mb-5">
        <div className="flex gap-1 overflow-x-auto">
          {TABS.map((t) => {
            const TIcon = t.Icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={cn(
                  "px-3 py-2.5 -mb-px flex items-center gap-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap",
                  active
                    ? "border-brand-600 text-brand-600"
                    : "border-transparent text-ink-500 hover:text-ink-800 dark:hover:text-ink-200",
                )}
              >
                <TIcon size={14} /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {analytics.isLoading || !analytics.data ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-24" />
            ))}
          </div>
          <Skeleton className="h-72 w-full" />
        </div>
      ) : (
        <>
          {tab === "overview" && <OverviewTab d={analytics.data} />}
          {tab === "networks" && <NetworksTab d={analytics.data} />}
          {tab === "sentiment" && <SentimentTab d={analytics.data} />}
          {tab === "reports" && <ReportsTab projectName={analytics.data.project_name} />}
        </>
      )}
    </div>
  );
}

// ============================================================
// TAB 1 — Resumen general
// ============================================================

function OverviewTab({ d }: { d: ProjectAnalytics }) {
  const totalSent = Math.max(d.sentiment.pos + d.sentiment.neg + d.sentiment.neu, 1);
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <KpiBig
          label="Menciones totales"
          value={formatNumber(d.total_mentions)}
          delta={d.delta_pct != null ? `${d.delta_pct >= 0 ? "+" : ""}${d.delta_pct}%` : undefined}
          tone={d.delta >= 0 ? "good" : "bad"}
          sub={`suma de ${d.per_network.length} redes`}
        />
        <KpiBig
          label="Alcance total"
          value={d.reach >= 1_000_000 ? `${(d.reach / 1_000_000).toFixed(1)} M` : formatNumber(d.reach)}
          tone="good"
          sub="impresiones estimadas"
        />
        <KpiBig
          label="Engagement"
          value={`${d.engagement_rate}%`}
          tone="good"
          sub={`${formatNumber(d.engagement)} interacciones`}
        />
        <KpiBig
          label="Sentimiento neto"
          value={`${d.sentiment.nss >= 0 ? "+" : ""}${d.sentiment.nss}`}
          tone={d.sentiment.nss >= 0 ? "good" : "bad"}
          sub={`${Math.round((d.sentiment.pos / totalSent) * 100)}% positivo`}
        />
      </div>

      {/* Per-network breakdown */}
      {d.per_network.length === 0 ? (
        <Card className="text-center py-12 mb-5">
          <p className="text-sm text-ink-500">
            Aún no hay menciones en este período. Configura las fuentes del proyecto para empezar.
          </p>
        </Card>
      ) : (
        <Card className="!p-0 mb-5 overflow-hidden">
          <div className="px-5 py-3 border-b border-ink-100 dark:border-ink-800 flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-ink-900 dark:text-white">Desglose por red social</h3>
              <p className="text-xs text-ink-500">Indicadores clave de cada red configurada</p>
            </div>
            <span className="text-xs text-ink-500">{d.range_days} días</span>
          </div>
          <div
            className={cn(
              "grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 divide-ink-100 dark:divide-ink-800",
              d.per_network.length >= 4
                ? "xl:grid-cols-4 xl:divide-x"
                : d.per_network.length === 3
                ? "lg:grid-cols-3 lg:divide-x"
                : "sm:divide-x",
            )}
          >
            {d.per_network.map((n) => (
              <NetworkKpiBlock key={n.network} n={n} />
            ))}
          </div>
        </Card>
      )}

      {/* Volume stacked + Share donut */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
        <Card className="lg:col-span-2">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div>
              <h3 className="font-semibold text-ink-900 dark:text-white">Volumen diario por red</h3>
              <p className="text-xs text-ink-500">{d.range_days} días · apilado</p>
            </div>
            <div className="flex items-center gap-3 text-[11px] flex-wrap">
              {d.per_network.map((n) => (
                <span key={n.network} className="flex items-center gap-1.5">
                  <span
                    className="w-2 h-2 rounded-sm"
                    style={{ background: netMeta(n.network).color }}
                  />
                  <span className="text-ink-600 dark:text-ink-300">{netMeta(n.network).label}</span>
                </span>
              ))}
            </div>
          </div>
          <StackedVolumeChart per_network={d.per_network} />
        </Card>

        <Card>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-ink-900 dark:text-white">Share por red</h3>
            <Badge variant="muted">% volumen</Badge>
          </div>
          <ShareDonut per_network={d.per_network} total={d.total_mentions} />
          <div className="mt-4 space-y-2">
            {d.per_network.map((n) => (
              <div key={n.network} className="flex items-center gap-3">
                <SocialDot network={n.network} size={18} />
                <span className="flex-1 text-sm text-ink-700 dark:text-ink-200">
                  {netMeta(n.network).label}
                </span>
                <span className="text-xs text-ink-500 font-mono">{n.share}%</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Sentiment by network */}
      <Card className="mb-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <h3 className="font-semibold text-ink-900 dark:text-white">Sentimiento por red</h3>
            <p className="text-xs text-ink-500">Distribución pos / neu / neg</p>
          </div>
          <Badge variant={d.sentiment.nss >= 0 ? "good" : "bad"}>
            {d.sentiment.nss >= 0 ? "Positivo dominante" : "Negativo dominante"}
          </Badge>
        </div>
        <SentimentStackedChart per_network={d.per_network} />
      </Card>

      {/* Top content per network */}
      {Object.keys(d.top_per_network).length > 0 && (
        <Card className="!p-0 mb-5 overflow-hidden">
          <div className="px-5 py-3 border-b border-ink-100 dark:border-ink-800">
            <h3 className="font-semibold text-ink-900 dark:text-white">Top contenido por red</h3>
            <p className="text-xs text-ink-500">Mejor publicación por red en este período</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 divide-y xl:divide-y-0 xl:divide-x divide-ink-100 dark:divide-ink-800">
            {Object.entries(d.top_per_network).map(([net, post]) => (
              <TopPostCard key={net} network={net} post={post} />
            ))}
          </div>
        </Card>
      )}
    </>
  );
}

// ============================================================
// TAB 2 — Por red social (deep dive)
// ============================================================

function NetworksTab({ d }: { d: ProjectAnalytics }) {
  const [selected, setSelected] = useState<string>(
    d.per_network[0]?.network ?? d.project_networks[0] ?? "x",
  );
  const current = d.per_network.find((n) => n.network === selected);

  if (d.per_network.length === 0) {
    return (
      <Card className="text-center py-12">
        <p className="text-sm text-ink-500">No hay datos por red todavía.</p>
      </Card>
    );
  }

  return (
    <>
      {/* Network picker */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        {d.per_network.map((n) => {
          const meta = netMeta(n.network);
          const isSel = selected === n.network;
          return (
            <button
              key={n.network}
              type="button"
              onClick={() => setSelected(n.network)}
              className={cn(
                "text-left rounded-xl2 ring-1 p-4 bg-white dark:bg-ink-900 transition-all overflow-hidden relative",
                isSel
                  ? "ring-2 ring-brand-500 shadow-pop"
                  : "ring-ink-200/60 dark:ring-ink-700/60 hover:shadow-card",
              )}
            >
              <div
                style={{ background: meta.color }}
                className="absolute left-0 right-0 top-0 h-[3px]"
              />
              <div className="flex items-center gap-3 mt-1">
                <div
                  style={{ background: meta.color }}
                  className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                >
                  <SocialDot network={n.network} size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-ink-900 dark:text-white truncate">
                    {meta.label}
                  </div>
                  <div className="text-[11px] text-ink-500">
                    {formatNumber(n.volume)} menciones · {n.share}%
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {current && <NetworkDeepDive n={current} top={d.top_per_network[current.network]} />}
    </>
  );
}

function NetworkDeepDive({ n, top }: { n: NetworkBreakdown; top: TopNetworkPost | undefined }) {
  const meta = netMeta(n.network);

  return (
    <>
      <div className="mb-3 flex items-center gap-3">
        <div
          style={{ background: meta.color }}
          className="w-12 h-12 rounded-xl flex items-center justify-center"
        >
          <SocialDot network={n.network} size={24} />
        </div>
        <div>
          <h2 className="text-xl font-bold text-ink-900 dark:text-white">Detalle · {meta.label}</h2>
          <p className="text-xs text-ink-500">Indicadores específicos de esta red</p>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
        <KpiCompact label="Menciones" value={formatNumber(n.volume)} />
        <KpiCompact
          label="Alcance"
          value={n.reach >= 1_000_000 ? `${(n.reach / 1_000_000).toFixed(1)}M` : `${(n.reach / 1000).toFixed(1)}k`}
        />
        <KpiCompact label="Engagement" value={`${n.engagement_rate}%`} />
        <KpiCompact
          label="Sentimiento"
          value={`${n.sentiment.nss >= 0 ? "+" : ""}${n.sentiment.nss}`}
          tone={n.sentiment.nss >= 0 ? "good" : "bad"}
        />
        <KpiCompact label="Share total" value={`${n.share}%`} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
        <Card className="lg:col-span-2">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h3 className="font-semibold text-ink-900 dark:text-white">Volumen diario · {meta.label}</h3>
            <Badge variant="brand">Tendencia</Badge>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={n.daily}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,23,42,0.06)" vertical={false} />
              <XAxis dataKey="day" tickFormatter={(v) => v.slice(5, 10)} stroke="#9CA3AF" fontSize={11} />
              <YAxis stroke="#9CA3AF" fontSize={11} />
              <Tooltip />
              <Line
                type="monotone"
                dataKey="count"
                stroke={meta.color}
                strokeWidth={2.2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        <Card>
          <h3 className="font-semibold text-ink-900 dark:text-white mb-3">Sentimiento · {meta.label}</h3>
          <SentimentDonut pos={n.sentiment.pos_pct} neu={n.sentiment.neu_pct} neg={n.sentiment.neg_pct} />
          <div className="space-y-2 mt-3">
            {(
              [
                ["Positivo", n.sentiment.pos_pct, "#10B981"],
                ["Neutral", n.sentiment.neu_pct, "#9CA3AF"],
                ["Negativo", n.sentiment.neg_pct, "#EF4444"],
              ] as const
            ).map(([l, v, c]) => (
              <div key={l} className="flex items-center gap-2 text-sm">
                <span className="w-2 h-2 rounded-full" style={{ background: c }} />
                <span className="flex-1 text-ink-700 dark:text-ink-200">{l}</span>
                <span className="font-semibold text-ink-900 dark:text-white">{v}%</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Top content */}
      {top && (
        <Card className="!p-0">
          <div className="px-5 py-3 border-b border-ink-100 dark:border-ink-800">
            <h3 className="font-semibold text-ink-900 dark:text-white">Top contenido · {meta.label}</h3>
          </div>
          <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-5">
            <div className="md:col-span-2">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-ink-100 dark:bg-ink-800 text-ink-600 text-sm font-bold flex items-center justify-center">
                  {(top.author_handle ?? "?").slice(0, 1).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="text-base font-semibold text-ink-900 dark:text-white">
                    {top.author_handle ?? "Sin autor"}
                  </div>
                  <div className="text-xs text-ink-500 flex items-center gap-1.5">
                    <SocialDot network={n.network} size={11} />
                    {new Date(top.published_at).toLocaleString("es-PE")}
                  </div>
                </div>
              </div>
              <p className="text-base text-ink-800 dark:text-ink-100 leading-relaxed">{top.text}</p>
              <div className="mt-4 flex items-center gap-2 flex-wrap">
                {top.url && (
                  <a href={top.url} target="_blank" rel="noreferrer">
                    <Button variant="outline" size="sm">
                      <ExternalLink size={14} /> Ver original
                    </Button>
                  </a>
                )}
                <Button variant="outline" size="sm" disabled>
                  <Reply size={14} /> Responder
                </Button>
                <Button variant="ghost" size="sm" disabled>
                  <Bookmark size={14} /> Guardar
                </Button>
              </div>
            </div>
            <div className="space-y-3">
              <Stat2 label="Alcance" value={formatNumber(top.reach)} />
              <Stat2 label="Engagement" value={formatNumber(top.engagement)} />
              <Stat2
                label="Sentimiento"
                value={top.sentiment === "pos" ? "Positivo" : top.sentiment === "neg" ? "Negativo" : "Neutral"}
              />
            </div>
          </div>
        </Card>
      )}
    </>
  );
}

// ============================================================
// TAB 3 — Sentimiento (cross-network)
// ============================================================

function SentimentTab({ d }: { d: ProjectAnalytics }) {
  const totalSent = Math.max(d.sentiment.pos + d.sentiment.neg + d.sentiment.neu, 1);
  const pos = Math.round((d.sentiment.pos / totalSent) * 100);
  const neu = Math.round((d.sentiment.neu / totalSent) * 100);
  const neg = 100 - pos - neu;

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
        <Card>
          <h3 className="font-semibold text-ink-900 dark:text-white mb-1">Distribución actual</h3>
          <p className="text-xs text-ink-500 mb-4">
            {d.range_days} días · todas las redes del proyecto
          </p>
          <div className="flex items-center justify-center py-3">
            <SentimentDonut pos={pos} neu={neu} neg={neg} size={140} />
          </div>
          <div className="space-y-2 mt-4">
            {(
              [
                ["Positivo", pos, "#10B981"],
                ["Neutral", neu, "#9CA3AF"],
                ["Negativo", neg, "#EF4444"],
              ] as const
            ).map(([l, v, c]) => (
              <div key={l} className="flex items-center gap-3 text-sm">
                <span className="w-2 h-2 rounded-full" style={{ background: c }} />
                <span className="flex-1 text-ink-700 dark:text-ink-200">{l}</span>
                <span className="font-semibold text-ink-900 dark:text-white">{v}%</span>
              </div>
            ))}
          </div>
        </Card>

        <Card className="lg:col-span-2">
          <h3 className="font-semibold text-ink-900 dark:text-white mb-1">Sentimiento neto · NSS</h3>
          <p className="text-xs text-ink-500 mb-3">(pos − neg) / total</p>
          <div className="flex items-baseline gap-2">
            <span
              className={cn(
                "text-5xl font-bold",
                d.sentiment.nss >= 0 ? "text-good" : "text-bad",
              )}
            >
              {d.sentiment.nss >= 0 ? "+" : ""}
              {d.sentiment.nss}
            </span>
            <span className="text-sm text-ink-500">/ 100</span>
          </div>
          <div className="mt-4">
            <div className="h-3 rounded-full bg-ink-100 dark:bg-ink-800 overflow-hidden flex">
              <div className="h-full bg-good" style={{ width: `${pos}%` }} />
              <div className="h-full bg-ink-400" style={{ width: `${neu}%` }} />
              <div className="h-full bg-bad" style={{ width: `${neg}%` }} />
            </div>
            <div className="flex justify-between text-[11px] text-ink-500 mt-1">
              <span>Positivo {pos}%</span>
              <span>Neutral {neu}%</span>
              <span>Negativo {neg}%</span>
            </div>
          </div>
        </Card>
      </div>

      <Card className="!p-0 mb-5">
        <div className="px-5 py-3 border-b border-ink-100 dark:border-ink-800">
          <h3 className="font-semibold text-ink-900 dark:text-white">Matriz de sentimiento por red</h3>
          <p className="text-xs text-ink-500">Distribución pos / neu / neg en cada red configurada</p>
        </div>
        <div className="p-5 overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-xs text-ink-500">
                <th className="text-left py-2 pr-4 font-medium">Red</th>
                <th className="py-2 px-2 font-medium">Volumen</th>
                <th className="py-2 px-2 font-medium" style={{ minWidth: 320 }}>
                  Distribución
                </th>
                <th className="py-2 px-2 font-medium text-right">NSS</th>
              </tr>
            </thead>
            <tbody>
              {d.per_network.map((n) => {
                const meta = netMeta(n.network);
                return (
                  <tr key={n.network} className="border-t border-ink-100 dark:border-ink-800">
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-2">
                        <SocialDot network={n.network} size={18} />
                        <span className="text-sm font-medium text-ink-800 dark:text-ink-100">
                          {meta.label}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-2 text-sm text-ink-700 dark:text-ink-200">
                      {formatNumber(n.volume)}
                    </td>
                    <td className="py-3 px-2">
                      <div className="flex h-6 rounded-md overflow-hidden ring-1 ring-ink-100 dark:ring-ink-800">
                        <div
                          style={{ width: `${n.sentiment.pos_pct}%`, background: "#10B981" }}
                          className="flex items-center justify-center text-[10px] text-white font-semibold"
                        >
                          {n.sentiment.pos_pct >= 8 ? `${n.sentiment.pos_pct}%` : ""}
                        </div>
                        <div
                          style={{ width: `${n.sentiment.neu_pct}%`, background: "#9CA3AF" }}
                          className="flex items-center justify-center text-[10px] text-white font-semibold"
                        >
                          {n.sentiment.neu_pct >= 8 ? `${n.sentiment.neu_pct}%` : ""}
                        </div>
                        <div
                          style={{ width: `${n.sentiment.neg_pct}%`, background: "#EF4444" }}
                          className="flex items-center justify-center text-[10px] text-white font-semibold"
                        >
                          {n.sentiment.neg_pct >= 8 ? `${n.sentiment.neg_pct}%` : ""}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-2 text-right">
                      <Badge
                        variant={
                          n.sentiment.nss > 40 ? "good" : n.sentiment.nss > 0 ? "muted" : "bad"
                        }
                      >
                        {n.sentiment.nss >= 0 ? "+" : ""}
                        {n.sentiment.nss}
                      </Badge>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}

// ============================================================
// TAB 4 — Reports (placeholder)
// ============================================================

function ReportsTab({ projectName }: { projectName: string }) {
  return (
    <Card className="!p-0">
      <CardHeader className="px-5 py-3 border-b border-ink-100 dark:border-ink-800">
        <CardTitle>Informes guardados</CardTitle>
        <CardDescription>Programados y bajo demanda · de {projectName}</CardDescription>
      </CardHeader>
      <CardContent className="p-8 text-center text-sm text-ink-500">
        Aún no tienes informes programados.
        <br />
        <span className="text-[11px]">Funcionalidad en desarrollo.</span>
      </CardContent>
    </Card>
  );
}

// ============================================================
// Subcomponents
// ============================================================

function KpiBig({
  label,
  value,
  delta,
  sub,
  tone = "good",
}: {
  label: string;
  value: string;
  delta?: string;
  sub?: string;
  tone?: "good" | "bad";
}) {
  return (
    <Card>
      <p className="text-xs text-ink-500">{label}</p>
      <div className="flex items-baseline gap-2 mt-1">
        <h2 className="text-3xl font-bold text-ink-900 dark:text-white tabular-nums">{value}</h2>
        {delta && (
          <span className={cn("text-xs font-semibold", tone === "bad" ? "text-bad" : "text-good")}>
            {delta}
          </span>
        )}
      </div>
      {sub && <p className="text-[11px] text-ink-500 mt-1">{sub}</p>}
    </Card>
  );
}

function KpiCompact({
  label,
  value,
  tone = "neutral",
}: {
  label: string;
  value: string;
  tone?: "good" | "bad" | "neutral";
}) {
  return (
    <Card className="!p-3">
      <p className="text-[11px] text-ink-500">{label}</p>
      <h3
        className={cn(
          "text-xl font-bold mt-0.5 tabular-nums",
          tone === "good" ? "text-good" : tone === "bad" ? "text-bad" : "text-ink-900 dark:text-white",
        )}
      >
        {value}
      </h3>
    </Card>
  );
}

function NetworkKpiBlock({ n }: { n: NetworkBreakdown }) {
  const meta = netMeta(n.network);
  return (
    <div className="p-5 relative">
      <div style={{ background: meta.color }} className="absolute left-0 top-0 bottom-0 w-1" />
      <div className="flex items-center gap-3 mb-3">
        <SocialDot network={n.network} size={26} />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-ink-900 dark:text-white truncate">
            {meta.label}
          </div>
          <div className="text-[10px] text-ink-500">{n.share}% del proyecto</div>
        </div>
      </div>
      <div className="space-y-2.5">
        <KpiLine label="Menciones" value={formatNumber(n.volume)} />
        <KpiLine
          label="Alcance"
          value={n.reach >= 1000 ? `${(n.reach / 1000).toFixed(0)}k` : formatNumber(n.reach)}
        />
        <KpiLine label="Engagement" value={`${n.engagement_rate}%`} />
        <KpiLine
          label="Sentimiento"
          value={`${n.sentiment.nss >= 0 ? "+" : ""}${n.sentiment.nss}`}
          tone={n.sentiment.nss > 30 ? "good" : n.sentiment.nss < 0 ? "bad" : "neutral"}
        />
        <div className="pt-1">
          <div className="text-[10px] uppercase tracking-wider text-ink-500 mb-1">
            Tendencia {n.daily.length}d
          </div>
          <Sparkline data={n.daily.map((d) => d.count)} color={meta.color} width={180} height={32} />
        </div>
      </div>
    </div>
  );
}

function KpiLine({
  label,
  value,
  tone = "neutral",
}: {
  label: string;
  value: string;
  tone?: "good" | "bad" | "neutral";
}) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-ink-500">{label}</span>
      <span
        className={cn(
          "font-semibold tabular-nums",
          tone === "good" ? "text-good" : tone === "bad" ? "text-bad" : "text-ink-900 dark:text-white",
        )}
      >
        {value}
      </span>
    </div>
  );
}

function Stat2({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between p-3 rounded-lg ring-1 ring-ink-100 dark:ring-ink-800">
      <span className="text-xs text-ink-500">{label}</span>
      <span className="text-base font-bold text-ink-900 dark:text-white tabular-nums">{value}</span>
    </div>
  );
}

function TopPostCard({ network, post }: { network: string; post: TopNetworkPost }) {
  const meta = netMeta(network);
  return (
    <div className="p-5">
      <div className="flex items-center gap-2 mb-3">
        <div
          style={{ background: meta.color }}
          className="w-7 h-7 rounded-md flex items-center justify-center"
        >
          <SocialDot network={network} size={16} />
        </div>
        <span className="text-xs font-semibold uppercase tracking-wide text-ink-700 dark:text-ink-200">
          {meta.label}
        </span>
      </div>
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full bg-ink-100 dark:bg-ink-800 text-ink-600 text-xs font-bold flex items-center justify-center shrink-0">
          {(post.author_handle ?? "?").slice(0, 1).toUpperCase()}
        </div>
        <div className="min-w-0">
          <div className="text-sm font-medium text-ink-900 dark:text-white truncate">
            {post.author_handle ?? "Sin autor"}
          </div>
          <div className="text-[11px] text-ink-500">
            {new Date(post.published_at).toLocaleDateString("es-PE")}
          </div>
        </div>
      </div>
      <p className="mt-3 text-sm text-ink-800 dark:text-ink-100 line-clamp-3 leading-relaxed">
        {post.text}
      </p>
      <div className="mt-3 grid grid-cols-3 gap-2 text-center">
        <SmallStat Icon={Heart} value={formatNumber(post.engagement)} />
        <SmallStat Icon={Repeat} value={formatNumber(post.reach)} />
        <SmallStat
          Icon={MessageCircle}
          value={post.sentiment === "pos" ? "+" : post.sentiment === "neg" ? "−" : "="}
        />
      </div>
      <div className="mt-3 text-[11px] text-ink-500 flex items-center justify-between">
        <span>Alcance · {formatNumber(post.reach)}</span>
        <Badge
          variant={post.sentiment === "pos" ? "good" : post.sentiment === "neg" ? "bad" : "muted"}
        >
          {post.sentiment === "pos" ? "Positivo" : post.sentiment === "neg" ? "Negativo" : "Neutral"}
        </Badge>
      </div>
    </div>
  );
}

function SmallStat({ Icon: IconCmp, value }: { Icon: typeof Heart; value: string }) {
  return (
    <div className="rounded-md bg-ink-50 dark:bg-ink-800 py-2">
      <IconCmp size={11} className="text-ink-500 mx-auto" />
      <div className="text-xs font-semibold text-ink-900 dark:text-white mt-0.5 tabular-nums">
        {value}
      </div>
    </div>
  );
}

// ----- Charts -----

function StackedVolumeChart({ per_network }: { per_network: NetworkBreakdown[] }) {
  // Merge daily series across networks by day
  const data = useMemo(() => {
    const days = new Set<string>();
    for (const n of per_network) for (const d of n.daily) days.add(d.day);
    const sorted = [...days].sort();
    return sorted.map((day) => {
      const row: Record<string, string | number> = { day: day.slice(5, 10) };
      for (const n of per_network) {
        row[n.network] = n.daily.find((d) => d.day === day)?.count ?? 0;
      }
      return row;
    });
  }, [per_network]);

  if (data.length === 0) {
    return (
      <div className="h-[280px] flex items-center justify-center text-sm text-ink-500">
        Sin datos en este período
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={280}>
      <AreaChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,23,42,0.06)" vertical={false} />
        <XAxis dataKey="day" stroke="#9CA3AF" fontSize={11} />
        <YAxis stroke="#9CA3AF" fontSize={11} />
        <Tooltip />
        {per_network.map((n) => (
          <Area
            key={n.network}
            type="monotone"
            dataKey={n.network}
            stackId="1"
            stroke={netMeta(n.network).color}
            fill={netMeta(n.network).color + "33"}
            strokeWidth={1.5}
          />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  );
}

function SentimentStackedChart({ per_network }: { per_network: NetworkBreakdown[] }) {
  const data = per_network.map((n) => ({
    name: netMeta(n.network).label,
    Positivo: n.sentiment.pos_pct,
    Neutral: n.sentiment.neu_pct,
    Negativo: n.sentiment.neg_pct,
  }));
  return (
    <ResponsiveContainer width="100%" height={260}>
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,23,42,0.06)" vertical={false} />
        <XAxis dataKey="name" stroke="#9CA3AF" fontSize={11} />
        <YAxis stroke="#9CA3AF" fontSize={11} domain={[0, 100]} />
        <Tooltip />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Bar dataKey="Positivo" stackId="s" fill="#10B981" radius={[0, 0, 0, 0]} />
        <Bar dataKey="Neutral" stackId="s" fill="#9CA3AF" />
        <Bar dataKey="Negativo" stackId="s" fill="#EF4444" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function ShareDonut({
  per_network,
  total,
  size = 180,
}: {
  per_network: NetworkBreakdown[];
  total: number;
  size?: number;
}) {
  const r = (size - 14) / 2;
  const c = 2 * Math.PI * r;
  let acc = 0;
  return (
    <div className="flex items-center justify-center py-2">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(15,23,42,0.06)" strokeWidth="14" />
        {per_network.map((n) => {
          const dash = (n.share / 100) * c;
          const offset = -(acc * c) / 100;
          acc += n.share;
          return (
            <circle
              key={n.network}
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              stroke={netMeta(n.network).color}
              strokeWidth="14"
              strokeDasharray={`${dash} ${c - dash}`}
              strokeDashoffset={offset}
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
            />
          );
        })}
        <text
          x={size / 2}
          y={size / 2 - 4}
          textAnchor="middle"
          fontSize="22"
          fontWeight="700"
          className="fill-ink-900 dark:fill-white"
        >
          {total >= 1000 ? `${(total / 1000).toFixed(1)}k` : total}
        </text>
        <text x={size / 2} y={size / 2 + 16} textAnchor="middle" fontSize="11" fill="#9CA3AF">
          menciones
        </text>
      </svg>
    </div>
  );
}

function SentimentDonut({
  pos,
  neu,
  neg,
  size = 120,
}: {
  pos: number;
  neu: number;
  neg: number;
  size?: number;
}) {
  const r = (size - 12) / 2;
  const c = 2 * Math.PI * r;
  const segs: [number, string][] = [
    [pos, "#10B981"],
    [neu, "#9CA3AF"],
    [neg, "#EF4444"],
  ];
  let acc = 0;
  return (
    <div className="flex items-center justify-center">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(15,23,42,0.06)" strokeWidth="10" />
        {segs.map(([v, color], i) => {
          const dash = (v / 100) * c;
          const offset = -(acc * c) / 100;
          acc += v;
          return (
            <circle
              key={i}
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              stroke={color}
              strokeWidth="10"
              strokeDasharray={`${dash} ${c - dash}`}
              strokeDashoffset={offset}
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
            />
          );
        })}
      </svg>
    </div>
  );
}
